// Auth context — OAuth2 PKCE + in-memory access token + silent refresh.
//
// Token lifecycle (docs/DASHBOARD.md, API.md §1):
//  - login():       generate PKCE verifier+challenge, stash verifier+state in sessionStorage
//                   (ephemeral, deleted right after exchange — a verifier is NOT a token),
//                   redirect the browser to the provider's /auth/authorize.
//  - completeLogin(): on the /callback route, validate `state`, exchange `code`+`verifier`
//                   at /auth/token, store the access token IN MEMORY, delete the verifier.
//  - bootstrap:     on mount, try a silent refresh (/auth/refresh via httpOnly cookie) to
//                   restore a session across reloads without any stored access token.
//  - logout():      POST /auth/logout, clear the in-memory token.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiPost, refreshSession, setAuthLostHandler } from "@/lib/api";
import { getMe } from "@/lib/account";
import { setAccessToken } from "@/lib/token";
import { API_BASE, OAUTH_CLIENT_ID, redirectUri } from "@/lib/config";
import { challengeS256, randomString } from "@/lib/pkce";
import { COUPON_SESSION_KEY } from "@/lib/orders";
import type { TokenResponse, User } from "@/lib/types";

const VERIFIER_KEY = "ahcut.pkce.verifier";
const STATE_KEY = "ahcut.pkce.state";
const RETURN_KEY = "ahcut.pkce.return";
const REF_KEY = "ahcut.ref";

type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  /** The authenticated profile (GET /me) — single client-side source of truth.
   *  `role` gates the admin nav/routes (server-side authz is the authority). */
  user: User | null;
  /** True once the post-login /me fetch has settled (success or failure). */
  userReady: boolean;
  /** Re-fetch /me (e.g. after a verified email change). Throws on failure. */
  refreshUser: () => Promise<void>;
  /** Start the PKCE login redirect. `returnTo` is restored after callback. */
  login: (returnTo?: string) => Promise<void>;
  /** Handle the /callback query (code, state) → exchange → in-memory token. */
  completeLogin: (code: string, state: string) => Promise<string>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [userReady, setUserReady] = useState(false);
  // Guard so React 18/19 StrictMode double-invoke doesn't run bootstrap twice.
  const bootstrapped = useRef(false);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    // No cancel-on-cleanup flag: the ref guard already makes this run exactly once
    // across StrictMode's synchronous mount→unmount→remount, and AuthProvider lives
    // for the app's lifetime. A `cancelled` flag would be set true by the first
    // (synthetic) cleanup and never reset — the guard blocks re-entry — so the only
    // in-flight refresh's result gets discarded and status stays "loading" forever
    // (the "Menyiapkan sesi…" hang).

    // Referral capture (D9): marketing links land here as ?ref=CODE. Stash it
    // (ephemeral, NOT a credential) so login() forwards it to /auth/authorize,
    // where signup records referred_by_code.
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) sessionStorage.setItem(REF_KEY, ref);

    // Coupon deep link (?coupon=CODE — e.g. a marketing/email link into /buy):
    // stash it the same way, but it is NOT forwarded to /auth/authorize — a
    // coupon isn't part of the auth grant, it's consumed by the Buy page after
    // login (auto-preview, then the key is cleared there).
    const coupon = new URLSearchParams(window.location.search).get("coupon");
    if (coupon) sessionStorage.setItem(COUPON_SESSION_KEY, coupon);

    (async () => {
      const ok = await refreshSession();
      setStatus(ok ? "authenticated" : "anonymous");
    })();
  }, []);

  // A failed mid-session refresh (api.ts) forces the app back to anonymous.
  useEffect(() => {
    setAuthLostHandler(() => {
      setAccessToken(null);
      setStatus("anonymous");
    });
    return () => setAuthLostHandler(null);
  }, []);

  // Load the profile whenever we become authenticated; clear it otherwise.
  // Unlike the bootstrap above, a `stale` cleanup flag is safe here: the effect
  // re-runs on every `status` change (StrictMode's remount re-fetches), so a
  // discarded result can never strand the app.
  useEffect(() => {
    if (status !== "authenticated") {
      setUser(null);
      setUserReady(false);
      return;
    }
    let stale = false;
    (async () => {
      try {
        const me = await getMe();
        if (!stale) setUser(me);
      } catch {
        // Surfaced by pages via userReady + user === null (a 401 already routed
        // through the auth-lost handler above).
        if (!stale) setUser(null);
      } finally {
        if (!stale) setUserReady(true);
      }
    })();
    return () => {
      stale = true;
    };
  }, [status]);

  const refreshUser = useCallback(async () => {
    setUser(await getMe());
  }, []);

  const login = useCallback(async (returnTo?: string) => {
    const verifier = randomString(32);
    const state = randomString(16);
    const challenge = await challengeS256(verifier);

    sessionStorage.setItem(VERIFIER_KEY, verifier);
    sessionStorage.setItem(STATE_KEY, state);
    if (returnTo) sessionStorage.setItem(RETURN_KEY, returnTo);

    const params = new URLSearchParams({
      client_id: OAUTH_CLIENT_ID,
      redirect_uri: redirectUri(),
      response_type: "code",
      code_challenge: challenge,
      code_challenge_method: "S256",
      state,
    });

    const ref = sessionStorage.getItem(REF_KEY);
    if (ref) params.set("ref", ref);

    window.location.assign(`${API_BASE}/auth/authorize?${params.toString()}`);
  }, []);

  const completeLogin = useCallback(async (code: string, state: string) => {
    const expectedState = sessionStorage.getItem(STATE_KEY);
    const verifier = sessionStorage.getItem(VERIFIER_KEY);
    if (!verifier || !expectedState || state !== expectedState) {
      throw new Error("Sesi login tidak valid atau kedaluwarsa. Silakan masuk ulang.");
    }

    let tokens: TokenResponse;
    try {
      tokens = await apiPost<TokenResponse>(
        "/auth/token",
        {
          grant_type: "authorization_code",
          code,
          code_verifier: verifier,
          redirect_uri: redirectUri(),
          client_id: OAUTH_CLIENT_ID,
        },
        { anonymous: true },
      );
    } finally {
      // The verifier has done its job — delete it whether or not the exchange
      // succeeded (never lingers as a credential).
      sessionStorage.removeItem(VERIFIER_KEY);
      sessionStorage.removeItem(STATE_KEY);
    }

    if (!tokens?.access_token) {
      throw new Error("Server tidak mengembalikan token akses.");
    }
    setAccessToken(tokens.access_token);
    setStatus("authenticated");

    const returnTo = sessionStorage.getItem(RETURN_KEY) ?? "/";
    sessionStorage.removeItem(RETURN_KEY);
    sessionStorage.removeItem(REF_KEY);
    return returnTo;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiPost<null>("/auth/logout");
    } catch {
      // Best-effort revoke; clear locally regardless.
    }
    setAccessToken(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, userReady, refreshUser, login, completeLogin, logout }),
    [status, user, userReady, refreshUser, login, completeLogin, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>.");
  return ctx;
}
